/**
 * FlowWidget — „Dokumenty w obiegu": słupki grupowe Nowe vs Zakończone w czasie
 * (wzór Figmy „Dokumenty w obiegu"). Okres przełącza segmentowy przełącznik w nagłówku kafelka.
 * Backend zwraca tylko niepuste kubełki; tutaj dopełniamy luki zerami (oś ciągła).
 * Kolory: tokeny `chart/1` (Nowe) i `chart/3` (Zakończone) z Figmy.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useChartTheme } from '@/lib/use-chart-theme';

import { classifyDataError, fetchFlow } from '../dashboard-api';
import type { ChartBucket, WidgetRenderProps } from '../types';

import { defaultBucket, fillGaps } from './ChartWidget';
import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const AXIS_TICK = { fontSize: 11, fill: 'rgb(var(--text-tertiary))' };
const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid rgb(var(--border-secondary))',
  background: 'rgb(var(--bg-elevated))',
  color: 'rgb(var(--text-primary))',
};

export function FlowWidget({ widget, period = 'year' }: WidgetRenderProps) {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const { isAll, mode, companyId } = useActiveCompanyScope();

  const bucket: ChartBucket =
    (widget.type === 'flow' ? widget.config.bucket : undefined) ?? defaultBucket(period);
  const blocked = mode === 'none';

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'flow', period, bucket, companyId ?? 'all'],
    queryFn: () => fetchFlow(period, bucket, isAll ? 'all' : 'company'),
    enabled: widget.type === 'flow' && !blocked,
    staleTime: 60_000,
    retry: false,
  });

  const data = useMemo(() => {
    const series = query.data?.series ?? [];
    return fillGaps(
      {
        created: series.map((p) => ({ bucket: p.bucket, count: p.created })),
        completed: series.map((p) => ({ bucket: p.bucket, count: p.completed })),
      },
      bucket,
    );
  }, [query.data, bucket]);

  if (widget.type !== 'flow') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );
  if (data.length === 0) return <WidgetEmpty message={t('tenant.dashboard.widgets.flow.empty')} />;

  return (
    <div className="relative h-full min-h-[160px] w-full">
      <div className="absolute inset-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis
              allowDecimals={false}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={32}
            />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: theme.track, opacity: 0.5 }} />
            <Legend
              verticalAlign="top"
              align="left"
              wrapperStyle={{ fontSize: 12, paddingBottom: 6 }}
              iconType="circle"
              iconSize={8}
            />
            <Bar
              dataKey="created"
              name={t('tenant.dashboard.widgets.flow.created')}
              fill={theme.palette[0]}
              radius={[3, 3, 0, 0]}
              maxBarSize={14}
            />
            <Bar
              dataKey="completed"
              name={t('tenant.dashboard.widgets.flow.completed')}
              fill={theme.palette[2]}
              radius={[3, 3, 0, 0]}
              maxBarSize={14}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
